import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { API_URL } from "@/lib/api";
import axios from "axios";
import { useThreadStream } from "@/lib/useThreadStream";

// This is what the client sees. It looks like a normal 1-on-1 chat with Mark.
// No hint that AI is drafting. Only "sent" outbound messages appear.

export default function PublicRelay() {
  const { threadId } = useParams();
  const [data, setData] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/relay/public/thread/${threadId}`);
      setData(r.data);
      setTimeout(() => scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 100);
    } catch { setNotFound(true); }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  // Live SSE — sanitized public stream (server only forwards inbound + sent outbound)
  useThreadStream(threadId, (evt) => {
    if (!evt?.payload) return;
    setData((prev) => {
      if (!prev) return prev;
      const msg = evt.payload;
      const existingIdx = prev.messages.findIndex((m) => m.id === msg.id);
      let messages;
      if (evt.kind === "message_updated" && existingIdx >= 0) {
        messages = prev.messages.map((m) => (m.id === msg.id ? { ...m, ...msg } : m));
      } else if (existingIdx === -1) {
        messages = [...prev.messages, msg];
      } else {
        messages = prev.messages;
      }
      // clear "typing" if we just got an outbound
      if (msg.direction === "outbound") setTyping(false);
      return { ...prev, messages };
    });
    setTimeout(() => scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
  }, /*isPublic=*/ true);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    setTyping(true);
    try {
      await axios.post(`${API_URL}/relay/public/thread/${threadId}/send`, { content: input });
      setInput("");
      // SSE will push the inbound + (if auto-approved) the outbound
    } finally { setSending(false); }
    // hide typing indicator after 20s if nothing comes (draft held for approval)
    setTimeout(() => setTyping(false), 20000);
  };

  if (notFound) {
    return (
      <div className="public-chat min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-2xl font-medium mb-2">Thread not found</div>
          <div className="text-sm text-gray-400">This conversation link is invalid or has expired.</div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="public-chat min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;

  const { thread, messages } = data;

  return (
    <div className="public-chat min-h-screen flex flex-col max-w-2xl mx-auto">
      <div className="border-b border-gray-800 p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center text-white font-medium">M</div>
        <div>
          <div className="font-medium">{thread.operator_name}</div>
          <div className="text-xs text-emerald-400">● Active now</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-sm text-gray-500 mt-10">
            Send a message to start the conversation.
          </div>
        )}
        {messages.map((m) => {
          const fromClient = m.direction === "inbound";
          return (
            <div key={m.id} className={`flex ${fromClient ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  fromClient ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-100"
                }`}
              >
                {m.content}
                <div className={`text-[10px] mt-1 ${fromClient ? "text-blue-200" : "text-gray-500"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
        {typing && (
          <div className="flex justify-start" data-testid="typing-indicator">
            <div className="bg-gray-800 text-gray-400 px-4 py-2 rounded-2xl text-sm">
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-pulse"></span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-pulse" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-pulse" style={{ animationDelay: "300ms" }}></span>
              </span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-gray-800 p-3 flex gap-2">
        <input
          data-testid="public-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Message…"
          className="flex-1 bg-gray-900 border border-gray-800 rounded-full px-4 py-2 text-sm outline-none focus:border-emerald-600"
        />
        <button
          data-testid="public-send"
          disabled={sending || !input.trim()}
          className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium disabled:opacity-40"
        >
          {sending ? "…" : "Send"}
        </button>
      </form>
    </div>
  );
}
